import React, {useState} from 'react';
import {useForm} from 'react-hook-form';

import {useAuth} from '../Auth/useAuth';
import ProAccountFieldEdit, {ReferenceDataSelect} from './ProAccountFieldEdit';
import useGetLocalAuthorities from '../Queries/useGetLocalAuthorities';
import useHealthBoardAreas from '../Queries/useHealthBoardAreas';
import {PRO_REGION_TYPES} from '../Util/constants';

const REGION_LEGEND_ID = 'pro-account-region-legend';
const LOCAL_AUTHORITY_ID = 'pro-account-local-authority';
const HEALTH_BOARD_AREA_ID = 'pro-account-health-board-area';

const regionInputId = (regionType) =>
  `pro-account-region-${regionType
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')}`;

const ProAccountArea = () => {
  const {authState, updateProfessionalProfile} = useAuth();
  const profile = authState.user.profile;
  const [selectedRegionType, setSelectedRegionType] = useState(
    profile.local_authority_id
      ? 'Local Authority'
      : profile.health_board_area_id
      ? 'Health Board'
      : profile.national_scotland
      ? 'National (Scotland)'
      : 'Outside Scotland'
  );
  const onSubmit = async (data) => {
    // Based on the selectedRegionType, we may need to null other fields
    const update = {
      local_authority_id: selectedRegionType === 'Local Authority' ? data.local_authority_id : null,
      health_board_area_id:
        selectedRegionType === 'Health Board' ? data.health_board_area_id : null,
      national_scotland: selectedRegionType === 'National (Scotland)' ? true : false,
    };
    return await updateProfessionalProfile({...authState.user.profile, ...update});
  };
  const {
    register,
    handleSubmit,
    formState: {errors},
  } = useForm();

  const healthBoardAreasQuery = useHealthBoardAreas();
  const localAuthoritiesQuery = useGetLocalAuthorities({staleTime: Infinity});

  return (
    <ProAccountFieldEdit handleSubmit={handleSubmit} onSubmit={onSubmit} buttonDisabled={false}>
      {/* The choices are a group under their own prompt, so the prompt is read
          with each radio rather than merely sitting above them. */}
      <fieldset className="mb-6 border-0 p-0">
        <legend id={REGION_LEGEND_ID} className="mb-2">
          Please select the type of region that best reflects where you work:
        </legend>
        <div className="flex flex-wrap gap-x-6 gap-y-2">
          {PRO_REGION_TYPES.map((regionType) => (
            <div key={`region-type-${regionType}`} className="flex items-start gap-2">
              <input
                type="radio"
                id={regionInputId(regionType)}
                name="region_type"
                value={regionType}
                checked={selectedRegionType === regionType}
                onChange={(event) => setSelectedRegionType(event.target.value)}
                className="mt-3 size-5 shrink-0 accent-(--compass-primary) focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-(--compass-focus-ring)"
              />
              <label
                htmlFor={regionInputId(regionType)}
                className="flex min-h-11 cursor-pointer items-center py-2.5 text-(--compass-on-surface)">
                {regionType}
              </label>
            </div>
          ))}
        </div>
      </fieldset>
      {selectedRegionType === 'Local Authority' && (
        <ReferenceDataSelect
          id={LOCAL_AUTHORITY_ID}
          testId={LOCAL_AUTHORITY_ID}
          label="Local Authority"
          placeholder="Select a local authority"
          query={localAuthoritiesQuery}
          options={localAuthoritiesQuery.data}
          defaultValue={profile.local_authority_id ?? ''}
          field={register('local_authority_id', {required: true})}
          invalid={!!errors.local_authority_id}
          errorMessage={errors.local_authority_id && 'Please select your local authority.'}
        />
      )}
      {selectedRegionType === 'Health Board' && (
        <ReferenceDataSelect
          id={HEALTH_BOARD_AREA_ID}
          testId={HEALTH_BOARD_AREA_ID}
          label="Health Board Area"
          placeholder="Select a health board area"
          query={healthBoardAreasQuery}
          options={healthBoardAreasQuery.data}
          defaultValue={profile.health_board_area_id ?? ''}
          field={register('health_board_area_id', {required: true})}
          invalid={!!errors.health_board_area_id}
          errorMessage={errors.health_board_area_id && 'Please select your health board area.'}
        />
      )}
    </ProAccountFieldEdit>
  );
};

export default ProAccountArea;
